import React, { useCallback, useEffect, useState } from 'react';
import api from './api';
import AyaLogo from './AyaLogo';
import {
  projectCode,
  formatDate,
  formatMontant,
  userDisplayName,
  PROJECT_PRIORITY_LABELS,
} from './helpers';

// Reprend les rubriques du formulaire ER-PRE4-33 V2.0 du système QSE.
const TYPES_CHEMINEMENT = {
  dmaic: 'Amélioration de procédé (DMAIC)',
  jalons: 'Mise en service / travaux (jalons)',
};

const ETAPES_DMAIC = [
  { cle: 'D', label: 'D — Définir', activites: 'Charte, VOC, SIPOC, cartographie du processus' },
  { cle: 'M', label: 'M — Mesurer', activites: 'Collecte de données, MSA, capabilité du processus' },
  { cle: 'A', label: 'A — Analyser', activites: 'Causes racines, tests d’hypothèses, Pareto' },
  { cle: 'I', label: 'I — Innover', activites: 'Solutions, pilote, plan d’implémentation' },
  { cle: 'C', label: 'C — Contrôler', activites: 'Plan de contrôle, standardisation, transfert' },
];

const ETAPES_JALONS = [
  { cle: 'J1', label: 'Jalon 1', activites: '' },
  { cle: 'J2', label: 'Jalon 2', activites: '' },
  { cle: 'J3', label: 'Jalon 3', activites: '' },
  { cle: 'J4', label: 'Jalon 4', activites: '' },
  { cle: 'J5', label: 'Jalon 5', activites: '' },
];

const TYPES_INDICATEUR = { Y: 'Y — Sortie (résultat)', X: 'X — Entrée (levier)' };

function ProjectCharter({ project, users, currentUser, onChanged }) {
  const [donnees, setDonnees] = useState(null);
  const [charte, setCharte] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState(null); // { commentaire }

  const charger = useCallback(() => {
    setChargement(true);
    api
      .get(`/projects/${project.id}/charte`)
      .then((res) => {
        setDonnees(res.data);
        setCharte(res.data.charte);
      })
      .catch(() => setErreur('Impossible de charger la charte.'))
      .finally(() => setChargement(false));
  }, [project.id]);

  useEffect(() => { charger(); }, [charger]);

  if (chargement) return <p>Chargement de la charte...</p>;
  if (!donnees || !charte) return <div className="error-message">{erreur || 'Charte indisponible.'}</div>;

  const modifiable = donnees.peut_rediger && donnees.etat !== 'validee';
  // Sur un document imprimé, une personne non désignée doit se lire comme un
  // manque à combler, pas comme un nom (« Inconnu »).
  const nomDe = (id) => {
    const u = users.find((x) => x.id === parseInt(id));
    return u ? userDisplayName(u) : '';
  };

  const champ = (nom) => (e) => setCharte({ ...charte, [nom]: e.target.value });

  const enregistrer = () => {
    setErreur(''); setMessage(''); setEnvoi(true);
    api
      .put(`/projects/${project.id}/charte`, { charte })
      .then(() => { setMessage('Brouillon enregistré.'); charger(); })
      .catch((err) => setErreur(err.response?.data?.message || "L'enregistrement a échoué."))
      .finally(() => setEnvoi(false));
  };

  const soumettre = () => {
    setErreur(''); setMessage(''); setEnvoi(true);
    api
      .put(`/projects/${project.id}/charte`, { charte })
      .then(() => api.post(`/projects/${project.id}/charte/soumettre`))
      .then(() => {
        setMessage('Charte soumise aux chefs de service. Les trois doivent signer avant la direction.');
        charger();
        if (onChanged) onChanged();
      })
      .catch((err) => setErreur(err.response?.data?.message || 'La soumission a échoué.'))
      .finally(() => setEnvoi(false));
  };

  const signer = (accepte, commentaire = '') => {
    setErreur(''); setMessage(''); setEnvoi(true);
    api
      .post(`/projects/${project.id}/charte/valider`, { accepte, commentaire })
      .then(() => {
        setRefus(null);
        setMessage(accepte ? 'Validation enregistrée.' : 'Charte renvoyée pour correction.');
        charger();
        if (onChanged) onChanged();
      })
      .catch((err) => setErreur(err.response?.data?.message || 'La validation a échoué.'))
      .finally(() => setEnvoi(false));
  };

  // --- Lignes d'indicateurs, d'équipe et d'étapes ---------------------------
  const majTableau = (nom, index, cle, valeur) => {
    const lignes = [...(charte[nom] || [])];
    lignes[index] = { ...lignes[index], [cle]: valeur };
    setCharte({ ...charte, [nom]: lignes });
  };
  const ajouterLigne = (nom, modele) =>
    setCharte({ ...charte, [nom]: [...(charte[nom] || []), modele] });
  const retirerLigne = (nom, index) =>
    setCharte({ ...charte, [nom]: (charte[nom] || []).filter((_, i) => i !== index) });

  const modeleEtapes = charte.type_cheminement === 'jalons' ? ETAPES_JALONS : ETAPES_DMAIC;

  const prefillEtapes = () => {
    setCharte({
      ...charte,
      etapes: modeleEtapes.map((e) => ({
        phase: e.label,
        activites: e.activites,
        livrables: '',
        echeance: '',
        responsable_id: '',
      })),
    });
  };

  const badgeEtat = {
    brouillon: 'badge-todo',
    validation_service: 'badge-review',
    validation_direction: 'badge-active',
    validee: 'badge-completed',
  }[donnees.etat] || 'badge-todo';

  return (
    <div className="charte">
      {/* --- Bandeau d'état et actions ------------------------------------- */}
      <div className="page-header-row charte-actions">
        <div>
          <h2 style={{ margin: 0 }}>Charte de projet</h2>
          <div style={{ marginTop: 6 }}>
            <span className={`badge ${badgeEtat}`}>{donnees.etat_label}</span>
            {donnees.soumise_le && (
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)', marginLeft: 8 }}>
                soumise le {formatDate(donnees.soumise_le)}
              </span>
            )}
            {donnees.validee_le && (
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)', marginLeft: 8 }}>
                validée le {formatDate(donnees.validee_le)}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {modifiable && (
            <button className="btn" onClick={enregistrer} disabled={envoi}>Enregistrer le brouillon</button>
          )}
          {modifiable && donnees.etat === 'brouillon' && (
            <button className="btn btn-primary" onClick={soumettre} disabled={envoi}>
              Soumettre à validation
            </button>
          )}
          {donnees.etat === 'validee' && (
            <button className="btn btn-primary" onClick={() => window.print()}>
              Imprimer la fiche de nomination
            </button>
          )}
        </div>
      </div>

      {erreur && <div className="error-message" style={{ marginBottom: 14 }}>{erreur}</div>}
      {message && <div className="success-message" style={{ marginBottom: 14 }}>{message}</div>}

      {/* --- Circuit de validation ----------------------------------------- */}
      <section className="rapport-section charte-circuit">
        <h3 className="rapport-section-titre">Circuit de validation</h3>
        <div className="charte-etapes">
          <div className={`charte-etape ${donnees.etat === 'brouillon' ? 'active' : 'faite'}`}>
            <div className="charte-etape-titre">1. Rédaction</div>
            <div className="charte-etape-sous">{nomDe(charte.charge_projet_id) || 'chargé de projet à désigner'}</div>
          </div>
          <div className={`charte-etape ${donnees.etat === 'validation_service' ? 'active' : donnees.etat === 'brouillon' ? '' : 'faite'}`}>
            <div className="charte-etape-titre">2. Chefs de service</div>
            <div className="charte-etape-sous">
              {donnees.services_attendus.length
                ? `${donnees.services_attendus.filter((s) => s.signee).length}/${donnees.services_attendus.length} signature(s)`
                : 'aucun service identifié'}
            </div>
          </div>
          <div className={`charte-etape ${donnees.etat === 'validation_direction' ? 'active' : donnees.etat === 'validee' ? 'faite' : ''}`}>
            <div className="charte-etape-titre">3. Direction d’usines</div>
            <div className="charte-etape-sous">
              {donnees.direction.map((d) => userDisplayName(d)).join(' ou ') || 'non désignée'}
            </div>
          </div>
        </div>

        {donnees.services_attendus.length > 0 && (
          <p className="charte-note" style={{ marginTop: 10, marginBottom: 0 }}>
            Les trois chefs de service signent, qu’ils aient ou non des équipes sur
            le projet : un projet d’usine engage tout le site. L’EIA, la Mécanique et
            le Bureau des méthodes relèvent du chef de service Maintenance.
          </p>
        )}

        {donnees.services_attendus.length > 0 && (
          <table className="rapport-tableau" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Service</th>
                <th>Signataire</th>
                <th>Équipes sur le projet</th>
                <th>État</th>
              </tr>
            </thead>
            <tbody>
              {donnees.services_attendus.map((s) => (
                <tr key={s.service}>
                  <td>{s.service}</td>
                  <td>
                    {s.signataire_name}
                    {s.par_interim && (
                      <span style={{ fontSize: 10.5, color: 'var(--accent)' }}>
                        {' '}(intérim de {s.chef_name})
                      </span>
                    )}
                  </td>
                  <td>
                    {s.concerne ? (
                      'oui'
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>
                        non — signature d’information
                      </span>
                    )}
                  </td>
                  <td>
                    {s.signee ? (
                      <span className="badge badge-completed">Signé</span>
                    ) : (
                      <span className="badge badge-todo">En attente</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {donnees.validations.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <h4 style={{ fontSize: 12, marginBottom: 6 }}>Historique</h4>
            {donnees.validations.map((v, i) => (
              <div key={i} className="charte-visa">
                <span className={`badge ${v.accepte ? 'badge-completed' : 'badge-blocked'}`}>
                  {v.accepte ? 'Validé' : 'Renvoyé'}
                </span>{' '}
                <strong>{v.user_name}</strong>
                {v.service ? ` — ${v.service}` : ' — direction'} · {formatDate(v.date)}
                {v.signature && (
                  <img src={v.signature} alt="" className="signature-visa signature-visa-petite" />
                )}
                {v.commentaire && <div className="charte-visa-motif">« {v.commentaire} »</div>}
              </div>
            ))}
          </div>
        )}

        {/* Bloc de signature, visible uniquement pour le signataire attendu */}
        {donnees.peut_signer && (
          <div className="charte-signature">
            <strong>Votre validation est attendue</strong>
            {donnees.peut_signer.service && ` pour le service ${donnees.peut_signer.service}`}.
            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={() => signer(true)} disabled={envoi}>
                Valider
              </button>
              <button className="btn btn-danger" onClick={() => setRefus({ commentaire: '' })} disabled={envoi}>
                Renvoyer pour correction
              </button>
            </div>
            {refus && (
              <div style={{ marginTop: 12 }}>
                <div className="form-group">
                  <label>Motif du renvoi (obligatoire)</label>
                  <textarea
                    value={refus.commentaire}
                    onChange={(e) => setRefus({ commentaire: e.target.value })}
                    placeholder="Indiquez ce qui doit être repris."
                  />
                </div>
                <button
                  className="btn btn-danger"
                  onClick={() => signer(false, refus.commentaire)}
                  disabled={envoi || !refus.commentaire.trim()}
                >
                  Confirmer le renvoi
                </button>{' '}
                <button className="btn" onClick={() => setRefus(null)}>Annuler</button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* --- Fiche de nomination, imprimable après validation --------------- */}
      {donnees.etat === 'validee' && (
        <section className="charte-nomination">
          <div className="rapport-entete">
            <AyaLogo className="rapport-logo" />
            <div>
              <div className="rapport-surtitre">AYA Gold &amp; Silver — Zgounder</div>
              <div className="rapport-titre">Fiche de nomination — {project.name}</div>
              <div className="rapport-soustitre">
                {projectCode(project.id)}{donnees.validee_le ? ` · Charte validée le ${formatDate(donnees.validee_le)}` : ''}
              </div>
            </div>
          </div>

          <p className="charte-nomination-texte">
            Le projet <strong>{project.name}</strong> ({projectCode(project.id)}) est officiellement
            lancé.{' '}
            {nomDe(charte.charge_projet_id) ? (
              <><strong>{nomDe(charte.charge_projet_id)}</strong> en est nommé chargé de projet et conduit</>
            ) : (
              <>Le chargé de projet <em>(à désigner dans la charte)</em> conduit</>
            )}{' '}
            l’équipe désignée ci-dessous pour la période du{' '}
            {formatDate(project.start_date)} au {formatDate(project.end_date)}.
          </p>

          {(charte.equipe || []).length > 0 && (
            <table className="rapport-tableau">
              <thead>
                <tr><th>Nom</th><th>Discipline</th><th>Rôle et responsabilités</th></tr>
              </thead>
              <tbody>
                {charte.equipe.map((m, i) => (
                  <tr key={i}>
                    <td>{m.user_id ? nomDe(m.user_id) : m.nom || '—'}</td>
                    <td>{users.find((u) => u.id === parseInt(m.user_id))?.department || m.discipline || '—'}</td>
                    <td>{m.role || '—'}{m.responsabilites ? ` — ${m.responsabilites}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <table className="rapport-tableau" style={{ marginTop: 14 }}>
            <thead>
              <tr><th>Visa</th><th>Nom</th><th>Date</th><th>Signature</th></tr>
            </thead>
            <tbody>
              {donnees.validations.filter((v) => v.accepte).map((v, i) => (
                <tr key={i}>
                  <td>{v.service ? `Chef de service — ${v.service}` : 'Direction d’usines'}</td>
                  <td>{v.user_name}</td>
                  <td>{formatDate(v.date)}</td>
                  <td>
                    {v.signature ? (
                      <img src={v.signature} alt={`Signature de ${v.user_name}`} className="signature-visa" />
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        validé dans l’application
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="rapport-pied">
            VIGIE · {projectCode(project.id)} — Fiche de nomination · AYA Gold &amp; Silver, Zgounder
          </div>
        </section>
      )}

      {/* --- Le formulaire -------------------------------------------------- */}
      <fieldset className="charte-formulaire" disabled={!modifiable}>
        <section className="rapport-section">
          <h3 className="rapport-section-titre">1. Identification</h3>
          <table className="rapport-fiche">
            <tbody>
              <tr>
                <th>Référence</th>
                <td>{projectCode(project.id)}</td>
                <th>Priorité</th>
                <td>{PROJECT_PRIORITY_LABELS[project.priority] || project.priority || '—'}</td>
              </tr>
              <tr>
                <th>Période</th>
                <td>{formatDate(project.start_date)} → {formatDate(project.end_date)}</td>
                <th>Budget estimé</th>
                <td>{project.budget_allocated ? formatMontant(project.budget_allocated) : 'Non renseigné'}</td>
              </tr>
            </tbody>
          </table>
          <p className="charte-note">
            Référence, période, priorité et budget se modifient dans « Modifier le projet ».
          </p>

          <div className="form-row" style={{ marginTop: 12 }}>
            <div className="form-group">
              <label>Chargé de projet</label>
              <select value={charte.charge_projet_id || ''} onChange={champ('charge_projet_id')}>
                <option value="">— Choisir —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{userDisplayName(u)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Zone / Département</label>
              <input value={charte.zone} onChange={champ('zone')} placeholder="Ex : Métallurgie / Usine Zgounder" />
            </div>
          </div>
        </section>

        <section className="rapport-section">
          <h3 className="rapport-section-titre">2. Description du problème ou de l’opportunité</h3>
          <div className="form-group">
            <label>Situation actuelle (AS-IS)</label>
            <textarea
              value={charte.situation_actuelle}
              onChange={champ('situation_actuelle')}
              placeholder="Quel est le problème ou l’écart constaté ? Quelles données l’objectivent ?"
            />
          </div>
          <div className="form-group">
            <label>Situation désirée (TO-BE)</label>
            <textarea
              value={charte.situation_desiree}
              onChange={champ('situation_desiree')}
              placeholder="Quelle amélioration chiffrée visez-vous ?"
            />
          </div>
        </section>

        <section className="rapport-section">
          <h3 className="rapport-section-titre">3. Impacts et efforts</h3>
          <div className="form-group">
            <label>Impacts pour l’entreprise</label>
            <textarea value={charte.impacts} onChange={champ('impacts')} placeholder="Gains financiers, sécurité, qualité, disponibilité..." />
          </div>
          <div className="form-group">
            <label>Efforts requis</label>
            <textarea value={charte.efforts} onChange={champ('efforts')} placeholder="Budget, ressources humaines, arrêts de production..." />
          </div>
        </section>

        <section className="rapport-section">
          <h3 className="rapport-section-titre">4. Portée du projet</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Inclus dans le périmètre</label>
              <textarea value={charte.inclus} onChange={champ('inclus')} placeholder="Processus, équipements, zones, livrables..." />
            </div>
            <div className="form-group">
              <label>Exclus du périmètre</label>
              <textarea value={charte.exclus} onChange={champ('exclus')} placeholder="Ce que le projet ne couvrira pas." />
            </div>
          </div>
        </section>

        <section className="rapport-section">
          <div className="page-header-row">
            <h3 className="rapport-section-titre" style={{ border: 'none', margin: 0 }}>
              5. Indicateurs Y = f(x)
            </h3>
            {modifiable && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => ajouterLigne('indicateurs', { type: 'Y', libelle: '', actuel: '', cible: '', unite: '' })}
              >
                + Ajouter un indicateur
              </button>
            )}
          </div>
          <table className="rapport-tableau">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Type</th>
                <th>Indicateur</th>
                <th style={{ width: 110 }}>Actuel</th>
                <th style={{ width: 110 }}>Cible</th>
                <th style={{ width: 90 }}>Unité</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {(charte.indicateurs || []).map((ind, i) => (
                <tr key={i}>
                  <td>
                    <select value={ind.type} onChange={(e) => majTableau('indicateurs', i, 'type', e.target.value)}>
                      {Object.entries(TYPES_INDICATEUR).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </td>
                  <td><input value={ind.libelle} onChange={(e) => majTableau('indicateurs', i, 'libelle', e.target.value)} /></td>
                  <td><input value={ind.actuel} onChange={(e) => majTableau('indicateurs', i, 'actuel', e.target.value)} /></td>
                  <td><input value={ind.cible} onChange={(e) => majTableau('indicateurs', i, 'cible', e.target.value)} /></td>
                  <td><input value={ind.unite} onChange={(e) => majTableau('indicateurs', i, 'unite', e.target.value)} /></td>
                  <td>
                    {modifiable && (
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => retirerLigne('indicateurs', i)}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(charte.indicateurs || []).length === 0 && (
            <p className="charte-note">Au moins un indicateur de sortie (Y) et deux leviers (X) sont recommandés.</p>
          )}
        </section>

        <section className="rapport-section">
          <div className="page-header-row">
            <h3 className="rapport-section-titre" style={{ border: 'none', margin: 0 }}>6. Équipe du projet</h3>
            {modifiable && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => ajouterLigne('equipe', { user_id: '', role: '', responsabilites: '' })}
              >
                + Ajouter un membre
              </button>
            )}
          </div>
          <p className="charte-note">
            Les services représentés ici déterminent qui devra valider la charte.
          </p>
          <table className="rapport-tableau">
            <thead>
              <tr>
                <th style={{ width: 200 }}>Membre</th>
                <th style={{ width: 150 }}>Discipline</th>
                <th>Rôle et responsabilités</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {(charte.equipe || []).map((m, i) => (
                <tr key={i}>
                  <td>
                    <select value={m.user_id || ''} onChange={(e) => majTableau('equipe', i, 'user_id', e.target.value)}>
                      <option value="">— Choisir —</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{userDisplayName(u)}</option>
                      ))}
                    </select>
                  </td>
                  <td>{users.find((u) => u.id === parseInt(m.user_id))?.department || '—'}</td>
                  <td>
                    <input
                      value={m.role}
                      onChange={(e) => majTableau('equipe', i, 'role', e.target.value)}
                      placeholder="Ex : expert métallurgie, référent HSE..."
                    />
                  </td>
                  <td>
                    {modifiable && (
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => retirerLigne('equipe', i)}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="rapport-section">
          <div className="page-header-row">
            <h3 className="rapport-section-titre" style={{ border: 'none', margin: 0 }}>7. Chemin critique</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select value={charte.type_cheminement} onChange={champ('type_cheminement')} className="btn btn-sm">
                {Object.entries(TYPES_CHEMINEMENT).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              {modifiable && (
                <button type="button" className="btn btn-sm" onClick={prefillEtapes}>
                  Pré-remplir les étapes
                </button>
              )}
            </div>
          </div>
          <p className="charte-note">
            DMAIC pour les projets d’amélioration de procédé ; jalons pour les mises en
            service et les travaux, où il n’y a rien à mesurer ni à analyser.
          </p>
          <table className="rapport-tableau">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Phase</th>
                <th>Activités</th>
                <th>Livrables</th>
                <th style={{ width: 120 }}>Échéance</th>
                <th style={{ width: 170 }}>Responsable</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {(charte.etapes || []).map((e, i) => (
                <tr key={i}>
                  <td><input value={e.phase} onChange={(ev) => majTableau('etapes', i, 'phase', ev.target.value)} /></td>
                  <td><input value={e.activites} onChange={(ev) => majTableau('etapes', i, 'activites', ev.target.value)} /></td>
                  <td><input value={e.livrables} onChange={(ev) => majTableau('etapes', i, 'livrables', ev.target.value)} /></td>
                  <td>
                    <input
                      value={e.echeance}
                      onChange={(ev) => majTableau('etapes', i, 'echeance', ev.target.value)}
                      placeholder="jj/mm/aaaa"
                    />
                  </td>
                  <td>
                    <select value={e.responsable_id || ''} onChange={(ev) => majTableau('etapes', i, 'responsable_id', ev.target.value)}>
                      <option value="">—</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{userDisplayName(u)}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {modifiable && (
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => retirerLigne('etapes', i)}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(charte.etapes || []).length === 0 && modifiable && (
            <p className="charte-note">
              Cliquez sur « Pré-remplir les étapes » pour partir du modèle correspondant au type choisi.
            </p>
          )}
        </section>

        <section className="rapport-section">
          <h3 className="rapport-section-titre">8. Critères de succès, risques et hypothèses</h3>
          <div className="form-group">
            <label>Critères de succès (SMART)</label>
            <textarea
              value={charte.criteres_succes}
              onChange={champ('criteres_succes')}
              placeholder="Spécifiques, mesurables, atteignables, réalistes, datés."
            />
          </div>
          <div className="form-group">
            <label>Risques identifiés et mesures de maîtrise</label>
            <textarea value={charte.risques} onChange={champ('risques')} placeholder="Risques techniques, financiers, humains, calendaires." />
          </div>
          <div className="form-group">
            <label>Conditions et hypothèses</label>
            <textarea value={charte.hypotheses} onChange={champ('hypotheses')} placeholder="Hypothèses de travail et conditions préalables au démarrage." />
          </div>
        </section>
      </fieldset>

      {modifiable && (
        <div className="page-header-row charte-actions" style={{ justifyContent: 'flex-end' }}>
          <button className="btn" onClick={enregistrer} disabled={envoi}>Enregistrer le brouillon</button>
          {donnees.etat === 'brouillon' && (
            <button className="btn btn-primary" onClick={soumettre} disabled={envoi} style={{ marginLeft: 8 }}>
              Soumettre à validation
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ProjectCharter;
